import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { X, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import clsx from 'clsx';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastOptions {
  variant?: ToastVariant;
  duration?: number;
}

interface ToastRecord {
  id: number;
  message: string;
  variant: ToastVariant;
  duration: number;
}

interface ToastContextValue {
  toast: (message: string, opts?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_STACK = 3;
const DEFAULT_DURATION = 4000;

const variantStyles: Record<ToastVariant, string> = {
  success: 'border-[color:var(--color-success)] text-[color:var(--color-success)]',
  error: 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]',
  info: 'border-ink-700 text-ink-700 dark:border-parchment-200 dark:text-parchment-200',
};

const VariantIcon = ({ variant }: { variant: ToastVariant }) => {
  const size = 18;
  if (variant === 'success') return <CheckCircle2 size={size} aria-hidden="true" />;
  if (variant === 'error') return <AlertCircle size={size} aria-hidden="true" />;
  return <Info size={size} aria-hidden="true" />;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const toast = useCallback<ToastContextValue['toast']>((message, opts) => {
    const id = ++idRef.current;
    const record: ToastRecord = {
      id,
      message,
      variant: opts?.variant ?? 'info',
      duration: opts?.duration ?? DEFAULT_DURATION,
    };
    setToasts((prev) => {
      const next = [...prev, record];
      return next.length > MAX_STACK ? next.slice(next.length - MAX_STACK) : next;
    });
    if (record.duration > 0) {
      const timer = setTimeout(() => dismiss(id), record.duration);
      timersRef.current.set(id, timer);
    }
  }, [dismiss]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2 sm:bottom-6 sm:right-6"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={clsx(
              'pointer-events-auto flex items-start gap-3 rounded-md border bg-[color:var(--color-bg-elevated)] px-3 py-2 shadow-md',
              'transition-all duration-200',
              variantStyles[t.variant],
            )}
          >
            <span className="mt-0.5 shrink-0">
              <VariantIcon variant={t.variant} />
            </span>
            <p className="flex-1 text-sm text-[color:var(--color-text)]">{t.message}</p>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
              className="shrink-0 rounded p-0.5 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
